import React, { useState } from "react";
import { observer } from "mobx-react";
import { PaginatorProps, Paginator } from "./Paginator";
import { SearchBox, SearchBoxProps } from "./SearchBox";
import { CreateButton } from "../buttons/CreateButton";
import { DuplicateButton } from "../buttons/DuplicateButton";
import { ExportButton } from "../buttons/ExportButton";
import {
  Button,
  Grid,
  IconButton,
  Tooltip,
  Snackbar,
  Paper,
} from "@mui/material";
import { StatusButton } from "../buttons/StatusButton";
import { ColumnMenu, ColumnMenuItem } from "./ColumnMenu";
import { useRootStore } from "../../../store/contexts";
import {
  CheckBoxIcon,
  CloseIcon,
  DeleteIcon,
  FilterListIcon,
  FullscreenExitIcon,
  FullscreenIcon,
  OpenInNewIcon,
  UploadFileIcon,
  ViewColumnIcon,
  ViewSidebarIcon,
} from "../../../icons";
export type ListToolboxProps = {
  paginatorProps?: PaginatorProps;
  searchBoxProps?: SearchBoxProps;
  createNewElement?: (event?: React.MouseEvent) => void;
  createNewLabel?: string;
  createNewLabelTooltip?: string;
  duplicateElement?: () => void;
  duplicateLabel?: string;
  duplicateLabelTooltip?: string;
  exportAll?: () => void;
  exportLabel?: string;
  exportLabelTooltip?: string;
  processing?: string | boolean;
  cancelProcess?: (() => void) | undefined;
  // Estados de UI del DataGrid (sincronizados via store)
  showColumnFilters?: boolean;
  onToggleFilters?: () => void;
  filtersLabel?: string;
  isFullScreen?: boolean;
  onToggleFullScreen?: () => void;
  viewMode?: "route" | "drawer";
  onToggleViewMode?: () => void;
  viewModeDrawerLabel?: string;
  viewModeRouteLabel?: string;
  columnsLabel?: string;
  // Props del menú de columnas
  columns?: ColumnMenuItem[];
  columnVisibility?: Record<string, boolean>;
  onColumnVisibilityChange?: (visibility: Record<string, boolean>) => void;
  columnOrder?: string[];
  onColumnOrderChange?: (order: string[]) => void;
  onResetColumnOrder?: () => void;
  onShowAllColumns?: () => void;
  onHideAllColumns?: () => void;
  onSaveFormat?: () => void;
  language?: "es" | "en" | "pt";
  // Batch create
  batchCreate?: () => void;
  batchCreateLabel?: string;
  batchCreateLabelTooltip?: string;
  // Multi-select
  enableMultiSelect?: boolean;
  multiSelectMode?: boolean;
  onToggleMultiSelect?: () => void;
  selectedCount?: number;
  onSelectAllPage?: () => void;
  onClearSelection?: () => void;
  onMultiDelete?: () => void;
  multiSelectLabel?: string;
  selectedCountLabel?: string;
  multiDeleteLabel?: string;
  cancelSelectionLabel?: string;
};

export const ListToolbox: React.FC<ListToolboxProps> = observer(
  ({
    paginatorProps,
    searchBoxProps,
    createNewElement,
    createNewLabel,
    createNewLabelTooltip,
    duplicateElement,
    duplicateLabel,
    duplicateLabelTooltip,
    exportAll,
    exportLabel,
    exportLabelTooltip,
    processing,
    cancelProcess,
    // Estados de UI del DataGrid
    showColumnFilters,
    onToggleFilters,
    filtersLabel,
    isFullScreen,
    onToggleFullScreen,
    viewMode,
    onToggleViewMode,
    viewModeDrawerLabel,
    viewModeRouteLabel,
    columnsLabel,
    // Props del menú de columnas
    columns,
    columnVisibility,
    onColumnVisibilityChange,
    columnOrder,
    onColumnOrderChange,
    onResetColumnOrder,
    onShowAllColumns,
    onHideAllColumns,
    onSaveFormat,
    language = "es",
    // Batch create
    batchCreate,
    batchCreateLabel,
    batchCreateLabelTooltip,
    // Multi-select
    enableMultiSelect,
    multiSelectMode,
    onToggleMultiSelect,
    selectedCount = 0,
    onSelectAllPage,
    onClearSelection,
    onMultiDelete,
    multiSelectLabel,
    selectedCountLabel,
    multiDeleteLabel,
    cancelSelectionLabel,
  }) => {
    const { prefStore, uiStore } = useRootStore();
    // Estado para el menú de columnas
    const [columnMenuAnchor, setColumnMenuAnchor] =
      useState<HTMLElement | null>(null);

    let searchBox;
    if (searchBoxProps) {
      searchBox = <SearchBox {...searchBoxProps} />;
    }

    let paginator;
    if (paginatorProps) {
      paginator = <Paginator {...paginatorProps} />;
    }

    let exportList;
    if (exportAll) {
      exportList = (
        <Tooltip title={exportLabelTooltip} placement="bottom" arrow>
          <div>
            <ExportButton onClick={() => exportAll()} label={exportLabel} />
          </div>
        </Tooltip>
      );
    }

    let createNew;
    if (createNewElement) {
      createNew = (
        <Tooltip title={createNewLabelTooltip} placement="bottom" arrow>
          <div onClick={(event) => createNewElement(event)}>
            <CreateButton label={createNewLabel} onClick={() => {}} />
          </div>
        </Tooltip>
      );
    }

    let batchCreateButton;
    if (batchCreate) {
      batchCreateButton = (
        <Tooltip title={batchCreateLabelTooltip} placement="bottom" arrow>
          <div>
            <Button
              variant="action"
              size={prefStore.toolbarSize}
              startIcon={<UploadFileIcon />}
              onClick={() => batchCreate()}
            >
              {batchCreateLabel}
            </Button>
          </div>
        </Tooltip>
      );
    }

    let duplicateAndEdit;
    if (duplicateElement) {
      duplicateAndEdit = (
        <Tooltip title={duplicateLabelTooltip} placement="bottom" arrow>
          <div>
            <DuplicateButton
              label={duplicateLabel}
              onClick={() => duplicateElement()}
            />
          </div>
        </Tooltip>
      );
    }

    const processingItem = (
      <Grid style={{ display: "flex", alignItems: "center" }}>
        {processing ? (
          <StatusButton
            label={processing === true ? "" : processing}
            onClick={cancelProcess}
          />
        ) : (
          <></>
        )}
      </Grid>
    );

    // Botones de toggle del DataGrid
    const toggleFiltersButton = onToggleFilters && (
      <Button
        variant={showColumnFilters ? "contained" : "action"}
        size={prefStore.toolbarSize}
        startIcon={<FilterListIcon />}
        onClick={onToggleFilters}
      >
        {filtersLabel || "Filtros"}
      </Button>
    );

    // Multi-select button and contextual bar
    const multiSelectButton = enableMultiSelect &&
      onToggleMultiSelect &&
      !multiSelectMode && (
        <Button
          variant="action"
          size={prefStore.toolbarSize}
          startIcon={<CheckBoxIcon />}
          onClick={onToggleMultiSelect}
        >
          {multiSelectLabel || "Selección"}
        </Button>
      );

    const toggleFullScreenButton = onToggleFullScreen && (
      <Tooltip
        title={
          isFullScreen ? "Salir de pantalla completa" : "Pantalla completa"
        }
        placement="bottom"
        arrow
      >
        <IconButton size="small" onClick={onToggleFullScreen}>
          {isFullScreen ? (
            <FullscreenExitIcon fontSize="small" />
          ) : (
            <FullscreenIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    );

    // Toggle viewMode: drawer (lateral preview) vs route (open in page).
    // Hidden in mobile because the drawer is not usable in small viewports.
    const toggleViewModeButton = onToggleViewMode && !uiStore.isMobile && (
      <Tooltip
        title={
          viewMode === "drawer"
            ? viewModeRouteLabel || "Abrir en pantalla completa al hacer clic"
            : viewModeDrawerLabel || "Abrir en panel lateral al hacer clic"
        }
        placement="bottom"
        arrow
      >
        <IconButton size="small" onClick={onToggleViewMode}>
          {viewMode === "drawer" ? (
            <ViewSidebarIcon fontSize="small" />
          ) : (
            <OpenInNewIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    );

    const hasColumnMenu = columns && columns.length > 0;
    const columnMenuButton = hasColumnMenu && (
      <Button
        variant="action"
        size={prefStore.toolbarSize}
        startIcon={<ViewColumnIcon />}
        onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
      >
        {columnsLabel || "Columnas"}
      </Button>
    );

    return (
      <>
        {/* Toolbar siempre visible */}
        <Grid container spacing={1} alignItems="center">
          <Grid item style={{ display: "flex", alignItems: "center" }}>
            {createNew}
          </Grid>
          <Grid item style={{ display: "flex", alignItems: "center" }}>
            {batchCreateButton}
          </Grid>
          <Grid item style={{ display: "flex", alignItems: "center" }}>
            {duplicateAndEdit}
          </Grid>
          <Grid item style={{ display: "flex", alignItems: "center" }}>
            {searchBox}
          </Grid>
          <Grid item style={{ display: "flex", alignItems: "center" }}>
            {columnMenuButton}
          </Grid>
          <Grid item style={{ display: "flex", alignItems: "center" }}>
            {toggleFiltersButton}
          </Grid>
          <Grid item style={{ display: "flex", alignItems: "center" }}>
            {multiSelectButton}
          </Grid>
          <Grid item style={{ display: "flex", alignItems: "center" }}>
            {paginator}
          </Grid>
          <Grid item style={{ display: "flex", alignItems: "center" }}>
            {exportList}
          </Grid>
          <Grid item style={{ flexGrow: 1 }} />
          <Grid item style={{ display: "flex", alignItems: "center" }}>
            {toggleViewModeButton}
          </Grid>
          <Grid item style={{ display: "flex", alignItems: "center" }}>
            {toggleFullScreenButton}
          </Grid>
          <Grid item>{processingItem}</Grid>
        </Grid>

        {/* Menú de columnas */}
        {hasColumnMenu && (
          <ColumnMenu
            anchorEl={columnMenuAnchor}
            open={Boolean(columnMenuAnchor)}
            onClose={() => setColumnMenuAnchor(null)}
            columns={columns}
            columnVisibility={columnVisibility || {}}
            onColumnVisibilityChange={onColumnVisibilityChange || (() => {})}
            columnOrder={columnOrder || []}
            onColumnOrderChange={onColumnOrderChange || (() => {})}
            onResetColumnOrder={onResetColumnOrder}
            onShowAllColumns={onShowAllColumns}
            onHideAllColumns={onHideAllColumns}
            onSaveFormat={onSaveFormat}
            language={language}
          />
        )}

        {/* Snackbar fijo para multi-selección */}
        <Snackbar
          open={multiSelectMode}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Paper
            elevation={6}
            sx={{
              px: 2,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 2,
              backgroundColor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            <span style={{ fontWeight: 500 }}>
              {selectedCountLabel ||
                `${selectedCount} seleccionado${
                  selectedCount !== 1 ? "s" : ""
                }`}
            </span>
            {onSelectAllPage && (
              <Button
                variant="text"
                size="small"
                onClick={onSelectAllPage}
                sx={{ color: "inherit", textTransform: "none" }}
              >
                Seleccionar página
              </Button>
            )}
            {onMultiDelete && selectedCount > 0 && (
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={onMultiDelete}
              >
                {multiDeleteLabel || "Eliminar"}
              </Button>
            )}
            {onClearSelection && (
              <Tooltip
                title={cancelSelectionLabel || "Cancelar selección"}
                placement="top"
                arrow
              >
                <IconButton
                  size="small"
                  onClick={onClearSelection}
                  sx={{ color: "inherit" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Paper>
        </Snackbar>
      </>
    );
  }
);
